import { apiClient, getErrorMessage } from './client'

export { getErrorMessage }

export interface Client {
  _id: string
  name: string
  type: 'studio' | 'formatPartner'
  aliases: string[]
  isActive: boolean
  externalIds: Record<string, string>
  createdBy: { userId: string; appId: string }
  updatedBy: { userId: string; appId: string }
  createdAt: string
  updatedAt: string
}

export interface ClientSearchResult {
  _id: string
  name: string
  type: 'studio' | 'formatPartner'
  aliases: string[]
  score: number
}

export interface ClientsListResponse {
  clients: Client[]
  total: number
  page: number
  pages: number
}

export interface ClientDuplicateWarning {
  status: 'duplicates_found'
  message: string
  matches: ClientSearchResult[]
}

export interface CreateClientData {
  name: string
  type: 'studio' | 'formatPartner'
  aliases?: string[]
  externalIds?: Record<string, string>
  confirmCreate?: boolean
}

export interface UpdateClientData {
  name?: string
  aliases?: string[]
  isActive?: boolean
}

// externalIds are managed via targeted endpoints — not part of PATCH
export interface UpdateClientExternalIdsData {
  externalIds: Record<string, string>
}

export type CreateClientResult =
  | { created: true; client: Client }
  | { created: false; duplicateWarning: ClientDuplicateWarning }

export async function listClients(params?: {
  type?: string
  isActive?: string
  search?: string
  page?: number
  limit?: number
}): Promise<ClientsListResponse> {
  const response = await apiClient.get<ClientsListResponse>('/admin/clients', { params })
  return response.data
}

export async function getClient(id: string): Promise<Client> {
  const response = await apiClient.get<{ client: Client }>(`/admin/clients/${id}`)
  return response.data.client
}

export async function createClient(data: CreateClientData): Promise<CreateClientResult> {
  const response = await apiClient.post<{ client?: Client } & Partial<ClientDuplicateWarning>>(
    '/admin/clients',
    data
  )

  if (response.data.status === 'duplicates_found') {
    return {
      created: false,
      duplicateWarning: response.data as ClientDuplicateWarning,
    }
  }

  return { created: true, client: response.data.client! }
}

export async function updateClient(id: string, data: UpdateClientData): Promise<Client> {
  const response = await apiClient.patch<{ client: Client }>(`/admin/clients/${id}`, data)
  return response.data.client
}

export async function deleteClient(id: string): Promise<void> {
  await apiClient.delete(`/admin/clients/${id}`)
}

export async function setClientExternalId(id: string, system: string, value: string): Promise<Client> {
  const response = await apiClient.put<{ client: Client }>(`/admin/clients/${id}/external-ids/${system}`, { id: value })
  return response.data.client
}

export async function removeClientExternalId(id: string, system: string): Promise<Client> {
  const response = await apiClient.delete<{ client: Client }>(`/admin/clients/${id}/external-ids/${system}`)
  return response.data.client
}
